import React, { useState } from "react";
import { Link } from "react-router-dom";
import { GripVertical, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { icraMerkez } from "../../lib/icraApi";

const KOLON_RENK = {
    BASLATILDI: "border-sky-500/40",
    TAKIPTE: "border-orange-500/40",
    TAHSIL_EDILDI: "border-emerald-500/40",
    KAPANDI: "border-slate-500/40",
};

export default function IcraKanban({ columns, onRefresh, loading }) {
    const [dragId, setDragId] = useState(null);
    const [busy, setBusy] = useState(false);

    const onDrop = async (colKey, item) => {
        if (!item?.id || item.dosya_durumu === colKey) return;
        setBusy(true);
        try {
            await icraMerkez.update(item.id, { dosya_durumu: colKey });
            toast.success("Durum güncellendi");
            onRefresh?.();
        } catch {
            toast.error("Güncelleme başarısız");
        } finally {
            setBusy(false);
            setDragId(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-orange-400" />
            </div>
        );
    }

    return (
        <div className="flex gap-3 overflow-x-auto pb-4 min-h-[420px]">
            {(columns || []).map((col) => (
                <div
                    key={col.key}
                    className={`flex-shrink-0 w-72 rounded-xl border bg-[#0B0F19]/60 ${KOLON_RENK[col.key] || "border-white/10"}`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                        const item = columns
                            .flatMap((c) => c.items || [])
                            .find((i) => i.id === dragId);
                        if (item) onDrop(col.key, item);
                    }}
                >
                    <div className="px-3 py-2 border-b border-white/10 flex justify-between items-center">
                        <span className="text-xs font-semibold text-slate-200">{col.label}</span>
                        <span className="text-[10px] text-slate-500">{col.count ?? 0}</span>
                    </div>
                    <div className="p-2 space-y-2 max-h-[520px] overflow-y-auto">
                        {(col.items || []).map((item) => (
                            <div
                                key={item.id}
                                draggable
                                onDragStart={() => setDragId(item.id)}
                                className="rounded-lg border border-white/10 bg-[#121A2F] p-2.5 cursor-grab active:cursor-grabbing hover:border-orange-500/30"
                            >
                                <div className="flex items-start gap-1.5">
                                    <GripVertical className="h-3.5 w-3.5 text-slate-600 mt-0.5 shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <Link
                                            to={`/e/icralar/${item.id}`}
                                            className="text-xs font-mono text-[#F3E5AB] hover:underline block truncate"
                                        >
                                            {item.icra_dosya_no || item.dosya_no || `#${item.id}`}
                                        </Link>
                                        <div className="text-[10px] text-slate-400 truncate mt-0.5">
                                            {item.muvekkil_ad_soyad || "—"}
                                        </div>
                                        <div className="text-[10px] text-slate-500 mt-1">
                                            Borç: {item.borc_tutari} TL
                                        </div>
                                        {item.tahsil_yuzde != null && (
                                            <div className="mt-1 h-1 rounded bg-white/10 overflow-hidden">
                                                <div
                                                    className="h-full bg-emerald-500/70"
                                                    style={{
                                                        width: `${Math.min(item.tahsil_yuzde, 100)}%`,
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            {busy && (
                <div className="fixed inset-0 z-40 bg-black/20 flex items-center justify-center pointer-events-none">
                    <Loader2 className="h-8 w-8 animate-spin text-orange-400" />
                </div>
            )}
        </div>
    );
}
